import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import { environment } from '@env';
import { AlertService } from '@v2/shared/components/alert/alert.service';
import { EventService } from '@v2/core/services/event.service';
import { TranslateExtensionService } from '@v2/core/services/translate-extension.service';
import { ApiResponse } from '@v2/data/schema/generated/ApiResponse';
import { NotificationPreferenceRequest } from '@v2/data/schema/generated/NotificationPreferenceRequest';
import { NotificationsSetting } from '@v2/data/schema/generated/NotificationsSetting';
import { SetNotificationSubscriptionPreferenceRequest } from '@v2/data/schema/generated/SetNotificationSubscriptionPreferenceRequest';
import { ChoiceComponent, ChoicesComponent } from '../../v2/shared/components/choice/choice.component';
import { ReturnBarComponent } from '../../v2/shared/components/return-bar/return-bar.component';
import { DateLabelComponent } from '../../v2/shared/components/date-label/date-label.component';
import { NoChangesModalComponent } from './no-changes-modal.component';

@Component({
  selector: 'app-notification-preferences',
  styleUrls: ['../layout/host.scss'],
  templateUrl: './notification-preferences.component.html',
  imports: [ReturnBarComponent, TranslatePipe, ChoiceComponent, ChoicesComponent, DateLabelComponent],
})
export class NotificationPreferencesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly alertService = inject(AlertService);
  private readonly eventService = inject(EventService);
  private readonly translateExtensionService = inject(TranslateExtensionService);
  private readonly modalService = inject(NgbModal);

  private readonly profileApiUrl = `${environment.apiBaseUrl}/web/v1/profile`;
  private loginToken = '';

  // Signals: the HTTP response callbacks can run outside the Angular zone (the MSAL
  // interceptor chain), and signal writes schedule change detection regardless.
  view = signal<'form' | 'confirmation'>('form');
  confirmationMode = signal<'unsubscribed' | 'subscribed'>('unsubscribed');
  emailAddress = signal<string | null>(null);
  receivingEmails = signal<boolean | null>(null);
  keepReceiving = 'false'; // the unsubscribe option is preselected
  saving = signal(false);

  ngOnInit(): void {
    this.loginToken = this.route.snapshot.queryParamMap.get('LoginToken') ?? '';

    if (!this.loginToken) {
      this.handleInvalidLink();
      return;
    }

    const request: NotificationPreferenceRequest = { LoginToken: this.loginToken };
    this.http
      .post<ApiResponse<NotificationsSetting>>(`${this.profileApiUrl}/subscriptionsettings`, request)
      .pipe(take(1))
      .subscribe({
        next: (response) => this.showSettings(response.value),
        error: () => this.handleInvalidLink(),
      });
  }

  cancel(): void {
    this.showNoChangesPopup();
  }

  save(): void {
    if (this.saving()) {
      return;
    }

    const wantsToReceive = this.keepReceiving === 'true';
    if (wantsToReceive && this.receivingEmails() === true) {
      // Already receiving, so saving with the "yes" option changes nothing (PBI 13455)
      this.showNoChangesPopup();
      return;
    }

    this.saving.set(true);
    const request: SetNotificationSubscriptionPreferenceRequest = {
      preferEmailNotifications: wantsToReceive,
      loginToken: this.loginToken,
    };
    this.http
      .post<ApiResponse<boolean>>(`${this.profileApiUrl}/notificationsubscriptionpreference`, request)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (wantsToReceive && response.value !== true) {
            // Re-subscribing only restores rows removed by an earlier unsubscribe; nothing was left to restore
            this.saving.set(false);
            this.alertService.error('edit-subscription-notifications.resubscribe-failed');
            return;
          }
          this.confirmationMode.set(wantsToReceive ? 'subscribed' : 'unsubscribed');
          this.view.set('confirmation');
        },
        error: () => {
          this.saving.set(false);
          this.alertService.error('edit-subscription-notifications.save-error');
        },
      });
  }

  private showSettings(settings: NotificationsSetting): void {
    this.emailAddress.set(settings.email);
    this.receivingEmails.set(settings.receiveEmailNotifications === true);

    // The API returns a culture code like "nl-NL"; only "nl" and "en" translation sets exist
    const languageCode = settings.languageCode?.substring(0, 2).toLowerCase();
    if (languageCode === 'nl' || languageCode === 'en') {
      this.eventService.languageChangedEvent.emit(languageCode);
      this.translateExtensionService.updateLanguageCode(languageCode);
    }
  }

  private showNoChangesPopup(): void {
    const modal = this.modalService.open(NoChangesModalComponent);
    modal.componentInstance.currentlyReceiving = this.receivingEmails() === true;
    modal.result.then(
      () => this.goToSignIn(),
      () => this.goToSignIn(),
    );
  }

  private handleInvalidLink(): void {
    this.alertService.error('edit-subscription-notifications.load-error');
    this.goToSignIn();
  }

  private goToSignIn(): void {
    this.router.navigate(['/sign-in']);
  }
}
